import React from 'react';
import { TrendingUp, TrendingDown, DollarSign, Receipt } from 'lucide-react';
import { ResponsiveAmount, AccountCodeLink } from '../../ui/ResponsiveAmount';

interface PnLWidgetProps {
  data: any;
}

/**
 * Classes SYSCOHADA pour le compte de résultat
 */
const PNL_ACCOUNTS = {
  produits: { code: '7', label: 'Classe 7 - Produits' },
  charges: { code: '6', label: 'Classe 6 - Charges' }
};

export function PnLWidget({ data }: PnLWidgetProps) {
  const pnl = data?.profitAndLoss?.current;
  const comparison = data?.profitAndLoss?.comparison;
  
  if (!pnl) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p className="text-sm">Aucune donnée disponible</p>
      </div>
    );
  }

  const netProfit = pnl.netProfit || 0;
  const revenue = pnl.revenue || 0;
  const expenses = pnl.expenses || 0;
  const profitMargin = pnl.profitMargin || (revenue > 0 ? (netProfit / revenue) * 100 : 0);
  const isPositive = netProfit >= 0;

  // Compare with previous period if available
  const previousNetProfit = comparison?.netProfit;
  const profitChange = previousNetProfit ? ((netProfit - previousNetProfit) / Math.abs(previousNetProfit)) * 100 : null;

  return (
    <div className="space-y-4">
      {/* Net Profit Header */}
      <div className="text-center pb-3 border-b border-border">
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Résultat Net</p>
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <ResponsiveAmount 
            value={netProfit}
            size="2xl"
            className="font-bold"
            colorize
          />
          {profitChange !== null && (
            <span className={`inline-flex items-center text-xs ${
              profitChange >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {profitChange >= 0 ? <TrendingUp className="h-3 w-3 mr-0.5" /> : <TrendingDown className="h-3 w-3 mr-0.5" />}
              {Math.abs(profitChange).toFixed(1)}%
            </span>
          )}
        </div>
      </div>

      {/* Revenue & Expenses */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-start gap-2">
          <div className="p-1.5 rounded-md bg-green-100 dark:bg-green-900/30 flex-shrink-0">
            <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              <p className="text-xs text-muted-foreground">Produits</p>
              <AccountCodeLink code={PNL_ACCOUNTS.produits.code} variant="subtle" />
            </div>
            <ResponsiveAmount 
              value={revenue}
              size="sm"
              className="text-green-600 dark:text-green-400 font-semibold"
              showCurrency={false}
            />
          </div>
        </div>
        
        <div className="flex items-start gap-2">
          <div className="p-1.5 rounded-md bg-red-100 dark:bg-red-900/30 flex-shrink-0">
            <Receipt className="h-4 w-4 text-red-600 dark:text-red-400" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              <p className="text-xs text-muted-foreground">Charges</p>
              <AccountCodeLink code={PNL_ACCOUNTS.charges.code} variant="subtle" />
            </div>
            <ResponsiveAmount 
              value={expenses}
              size="sm"
              className="text-red-600 dark:text-red-400 font-semibold"
              showCurrency={false}
            />
          </div>
        </div>
      </div>

      {/* Profit Margin */}
      <div className="pt-2">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-muted-foreground">Marge bénéficiaire</span>
          <span className={`font-medium tabular-nums ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {profitMargin.toFixed(1)}%
          </span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${
              isPositive ? 'bg-green-500' : 'bg-red-500'
            }`}
            style={{ width: `${Math.min(100, Math.abs(profitMargin))}%` }}
          />
        </div>
      </div>
    </div>
  );
}
